import FroalaEditor from "froala-editor";

export interface IRangeOffset {
    startOffset: number;
    endOffset: number;
}

export interface ISelectionSnapshot extends IRangeOffset {
    isCollapsed: boolean;
    startContainerPath: number[];
    endContainerPath: number[];
    startTextOffset: number;
    endTextOffset: number;
}

interface IResolvedBoundary {
    node: Node;
    offset: number;
}

export class FroalaActionUtilities {
    public static captureSelection(editor: FroalaEditor): ISelectionSnapshot | undefined {
        const selection = editor.doc.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return undefined;
        }

        const range = selection.getRangeAt(0);
        const root = editor.el;
        const startContainerPath = this.getNodePath(root, range.startContainer);
        const endContainerPath = this.getNodePath(root, range.endContainer);
        if (!startContainerPath || !endContainerPath) {
            return undefined;
        }

        // The snapshot stores:
        // - DOM path to the start and end containers.
        // - Native range offsets within those containers.
        // - Absolute text offsets across the editor as a fallback.
        return {
            isCollapsed: range.collapsed,
            startOffset: range.startOffset,
            endOffset: range.endOffset,
            startContainerPath,
            endContainerPath,
            startTextOffset: this.getTextOffset(root, range.startContainer, range.startOffset),
            endTextOffset: this.getTextOffset(root, range.endContainer, range.endOffset),
        };
    }

    public static restoreSelection(editor: FroalaEditor, snapshot?: ISelectionSnapshot): boolean {
        if (!snapshot) {
            return false;
        }

        // froala selection restore
        try {
            editor.selection.restore();
        } catch {
            // Froala markers are occasionally removed before restore is called.
        }

        if (this.selectionMatchesSnapshot(editor, snapshot)) {
            return true;
        }

        // if that fails (selection not matching snapshot)
        // - rebuild the range from the saved DOM path + offsets.
        // - if that also fails because nodes were recreated, rebuild from editor-wide text offsets.
        return this.tryRestoreByPath(editor, snapshot) || this.tryRestoreByTextOffset(editor, snapshot);
    }

    public static selectTextOffsets(editor: FroalaEditor, startTextOffset: number, endTextOffset: number): boolean {
        const root = editor.el;
        const start = this.resolveTextBoundary(root, startTextOffset);
        const end = this.resolveTextBoundary(root, endTextOffset);

        return this.applyRange(editor, start, end);
    }

    /**
     * Helper function for custom froala actions to restore the cursor after an action link has been added/edited.
     * @param editor froala editor.
     * @param anchorNode add/edit mode.
     * @param offsets where in the document the cursor is.
     */
    public static restoreCursor(editor: FroalaEditor, anchorNode: HTMLElement, offsets: IRangeOffset) {
        // even if blur event is disabled in this flow, still observed cases where froala marker (added in editor.selection.save())
        // was removed when shortcut keys are used.
        // restoring to the end of the last anchorNode as a fail-safe measure.
        editor.selection.restore();
        if (editor.selection.element() !== anchorNode) {
            editor.selection.setAtEnd(anchorNode);
            editor.selection.restore();
            // setAtEnd above will set the anchorOffset at the end of the node
            // - need to check offsets against this to address the case where element is set to fr-image and invalidates
            //   the previous offsets
            const endPosition = (editor.selection.get() as Selection).anchorOffset;
            if (editor.doc.getSelection().rangeCount > 0 && offsets.startOffset <= endPosition && offsets.endOffset <= endPosition) {
                const range = editor.doc.getSelection().getRangeAt(0);
                range.setStart(range.startContainer, offsets.startOffset);
                range.setEnd(range.endContainer, offsets.endOffset);
            }
        }
    }

    private static tryRestoreByPath(editor: FroalaEditor, snapshot: ISelectionSnapshot): boolean {
        const root = editor.el;
        const start = this.resolvePathBoundary(root, snapshot.startContainerPath, snapshot.startOffset);
        const end = this.resolvePathBoundary(root, snapshot.endContainerPath, snapshot.endOffset);

        return this.applyRange(editor, start, end, snapshot);
    }

    private static tryRestoreByTextOffset(editor: FroalaEditor, snapshot: ISelectionSnapshot): boolean {
        const root = editor.el;
        const start = this.resolveTextBoundary(root, snapshot.startTextOffset);
        const end = this.resolveTextBoundary(root, snapshot.endTextOffset);

        return this.applyRange(editor, start, end, snapshot);
    }

    private static applyRange(editor: FroalaEditor, start: IResolvedBoundary | undefined, end: IResolvedBoundary | undefined, snapshot?: ISelectionSnapshot): boolean {
        const selection = editor.doc.getSelection();
        if (!selection || !start || !end) {
            return false;
        }

        try {
            const range = editor.doc.createRange();
            range.setStart(start.node, start.offset);
            range.setEnd(end.node, end.offset);

            selection.removeAllRanges();
            selection.addRange(range);

            return snapshot ? this.selectionMatchesSnapshot(editor, snapshot) : true;
        } catch {
            return false;
        }
    }

    private static selectionMatchesSnapshot(editor: FroalaEditor, snapshot: ISelectionSnapshot): boolean {
        const restoredSnapshot = this.captureSelection(editor);
        if (!restoredSnapshot) {
            return false;
        }

        return restoredSnapshot.isCollapsed === snapshot.isCollapsed
            && restoredSnapshot.startTextOffset === snapshot.startTextOffset
            && restoredSnapshot.endTextOffset === snapshot.endTextOffset;
    }

    private static getNodePath(root: Node, node: Node): number[] | undefined {
        if (!this.isNodeWithinRoot(root, node)) {
            return undefined;
        }

        const path: number[] = [];
        let currentNode: Node | null = node;

        while (currentNode && currentNode !== root) {
            const parentNode: Node | null = currentNode.parentNode;
            if (!parentNode) {
                return undefined;
            }

            path.unshift(Array.prototype.indexOf.call(parentNode.childNodes, currentNode));
            currentNode = parentNode;
        }

        return path;
    }

    private static resolvePathBoundary(root: Node, path: number[], offset: number): IResolvedBoundary | undefined {
        let currentNode: Node = root;

        for (const childIndex of path) {
            const nextNode = currentNode.childNodes.item(childIndex);
            if (!nextNode) {
                return undefined;
            }

            currentNode = nextNode;
        }

        return {
            node: currentNode,
            offset: this.clampOffset(currentNode, offset),
        };
    }

    private static getTextOffset(root: Node, node: Node, offset: number): number {
        const ownerDocument = this.getOwnerDocument(root);
        if (!ownerDocument) {
            return 0;
        }

        const range = ownerDocument.createRange();
        range.selectNodeContents(root);
        range.setEnd(node, this.clampOffset(node, offset));

        return range.toString().length;
    }

    private static resolveTextBoundary(root: Node, textOffset: number): IResolvedBoundary {
        const ownerDocument = this.getOwnerDocument(root);
        if (!ownerDocument) {
            return {
                node: root,
                offset: this.clampOffset(root, textOffset),
            };
        }

        const walker = ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let remaining = Math.max(0, textOffset);
        let currentNode = walker.nextNode();
        let lastTextNode: Node | undefined;

        while (currentNode) {
            lastTextNode = currentNode;
            const textLength = currentNode.textContent?.length ?? 0;
            if (remaining <= textLength) {
                return {
                    node: currentNode,
                    offset: remaining,
                };
            }

            remaining -= textLength;
            currentNode = walker.nextNode();
        }

        if (lastTextNode) {
            return {
                node: lastTextNode,
                offset: lastTextNode.textContent?.length ?? 0,
            };
        }

        return {
            node: root,
            offset: root.childNodes.length,
        };
    }

    private static clampOffset(node: Node, offset: number): number {
        const maxOffset = node.nodeType === Node.TEXT_NODE
            ? node.textContent?.length ?? 0
            : node.childNodes.length;

        return Math.min(Math.max(0, offset), maxOffset);
    }

    private static isNodeWithinRoot(root: Node, node: Node): boolean {
        return root === node || (root instanceof HTMLElement && root.contains(node));
    }

    private static getOwnerDocument(root: Node): Document | undefined {
        if (root.ownerDocument) {
            return root.ownerDocument;
        }

        return root.nodeType === Node.DOCUMENT_NODE ? root as Document : undefined;
    }
}
